import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query';
import type { components } from '@/types/api.gen';

/**
 * Hooks für den geführten Inventar-Import (Spec S8-06 §3/§5, Kontrakte 3-5).
 *
 * Endpunkte:
 *   POST /inventory/import/preview          multipart `file` (YAML) -> Session + Report
 *   POST /inventory/import/{id}/apply       {accept: [item_id]}     -> ApplyResult
 *   GET  /inventory/import/sessions         Verlauf
 *   GET  /inventory/import/sessions/{id}    Session inkl. Report (Wiederaufnahme)
 *   POST /inventory/bw-map                  multipart `file` (TSV)  -> BwMapUploadResult
 *
 * Query-Keys der Import-Domäne bewusst LOKAL definiert (Muster:
 * systemSecretsKeys in useSystemSecrets.ts) — lib/query.ts gehört einem
 * parallelen Arbeitspaket und wird hier nur lesend importiert (Cross-
 * Invalidierung der Stammdaten-Caches nach Apply).
 */

// --- DTOs (API-Kontrakte 3-5) ----------------------------------------------

/** Wire-Schemas aus openapi (`pnpm gen:types` -> api.gen.ts) — Quelle der Wahrheit. */
type Wire = components['schemas'];

export type ImportReportCategory =
  | 'new_customers'
  | 'new_locations'
  | 'new_systems'
  | 'changed'
  | 'unmatched_tool_only'
  | 'conflicts';

export interface ImportFieldDiff {
  field: string;
  /** Wire lässt old/new offen — fehlend heißt "kein Wert" (UI rendert '–'). */
  old?: unknown;
  new?: unknown;
}

export interface ImportReportItem {
  item_id: string;
  kind: string;
  title: string;
  details?: unknown;
  diff?: ImportFieldDiff[] | null;
  default_selected: boolean;
}

/** Kategorien optional halten: tolerant gegen parallel gebautes Backend. */
export type InventoryImportReport = {
  [K in ImportReportCategory]?: ImportReportItem[] | null;
};

export type ImportSourceStats = Wire['InventoryImportSourceStats'];

export interface ImportPreviewResponse {
  session_id: string;
  filename: string;
  stats: ImportSourceStats;
  report: InventoryImportReport;
}

export interface ImportApplyError {
  item_id: string;
  message: string;
}

export interface ImportApplyResult {
  applied: number;
  skipped: number;
  errors?: ImportApplyError[] | null;
  summary?: string | null;
}

export interface ImportSessionSummary {
  id: string;
  filename: string;
  uploaded_at: string;
  /**
   * Wire-Name laut Kontrakt 3/openapi: `stats` — NICHT `source_stats` (so
   * heißt nur die DB-Spalte). Optional/nullable als Toleranz gegen alte
   * Sessions ohne persistierte Statistik.
   */
  stats?: ImportSourceStats | null;
  /** Item-Anzahl je Report-Kategorie. */
  counts?: Record<string, number>;
  applied_at?: string | null;
}

export interface ImportSessionDetail extends ImportSessionSummary {
  report: InventoryImportReport;
  /** openapi typisiert das Feld nur als loses Objekt — de facto ein ApplyResult. */
  applied_summary?: ImportApplyResult | null;
}

export interface ImportSessionListResponse {
  data: ImportSessionSummary[];
}

export interface ImportPreviewInput {
  file: File;
}

// --- Drift-Wächter gegen api.gen.ts ---------------------------------------
// Review S8-06/F4: der Verlauf zeigte '–', weil das DTO `source_stats`
// deklarierte, das Backend laut openapi aber `stats` liefert. Die Checks
// unten lassen tsc fehlschlagen, sobald ein generierter Wire-Typ den lokalen
// DTOs nicht mehr zuweisbar ist (z. B. nach `pnpm gen:types`).
// (SessionDetail ist bewusst nicht dabei: openapi typisiert dessen
// `applied_summary` nur als loses Objekt, unser DTO ist präziser.)
type AssertAssignable<Sub extends Super, Super> = Sub;
export type InventoryImportWireChecks = [
  AssertAssignable<Wire['InventoryImportPreviewResponse'], ImportPreviewResponse>,
  AssertAssignable<Wire['InventoryImportApplyResponse'], ImportApplyResult>,
  AssertAssignable<Wire['InventoryImportSessionSummary'], ImportSessionSummary>,
  AssertAssignable<Wire['InventoryImportSessionList'], ImportSessionListResponse>,
  AssertAssignable<Wire['BwMapUploadResult'], BwMapUploadResult>,
  // bw_item_id ist seit dem gen:types-Nachzug Teil des System-Schemas
  // (Regel-2-Lockerung 12.08.2026) — Wegfall soll tsc brechen lassen.
  AssertAssignable<Wire['System'], { bw_item_id?: string | null }>,
];

export interface ImportApplyInput {
  sessionId: string;
  accept: string[];
}

// --- Helpers ---------------------------------------------------------------

export function reportItems(
  report: InventoryImportReport,
  category: ImportReportCategory
): ImportReportItem[] {
  return report[category] ?? [];
}

/**
 * Initiale Checkbox-Auswahl für einen Report.
 *
 * `default_selected` wird für Neues + Änderungen respektiert; `conflicts`
 * und `unmatched_tool_only` sind NIE vorausgewählt (Spec §3 — unmatched ist
 * ohnehin reine Info ohne Checkbox, der Import löscht nie).
 */
export function defaultSelection(report: InventoryImportReport): Set<string> {
  const ids = new Set<string>();
  const preselectable: ImportReportCategory[] = [
    'new_customers',
    'new_locations',
    'new_systems',
    'changed',
  ];
  for (const category of preselectable) {
    for (const item of reportItems(report, category)) {
      if (item.default_selected) ids.add(item.item_id);
    }
  }
  return ids;
}

// --- Query-Keys ------------------------------------------------------------

export const inventoryImportKeys = {
  all: ['inventory-import'] as const,
  sessions: ['inventory-import', 'sessions'] as const,
  session: (sessionId: string) => ['inventory-import', 'session', sessionId] as const,
};

// --- Queries ---------------------------------------------------------------

/** Als plain Function exportiert, damit die Route sie via fetchQuery nutzen kann. */
export function fetchInventoryImportSession(sessionId: string): Promise<ImportSessionDetail> {
  return api.get<ImportSessionDetail>(`/inventory/import/sessions/${sessionId}`);
}

export function useInventoryImportSessions(): ReturnType<
  typeof useQuery<ImportSessionListResponse>
> {
  return useQuery<ImportSessionListResponse>({
    queryKey: inventoryImportKeys.sessions,
    queryFn: () => api.get<ImportSessionListResponse>('/inventory/import/sessions'),
    staleTime: 15_000,
  });
}

export function useInventoryImportSession(
  sessionId: string | undefined
): ReturnType<typeof useQuery<ImportSessionDetail>> {
  return useQuery<ImportSessionDetail>({
    queryKey: inventoryImportKeys.session(sessionId ?? ''),
    queryFn: () => fetchInventoryImportSession(sessionId ?? ''),
    enabled: Boolean(sessionId),
    staleTime: 15_000,
  });
}

// --- Mutations -------------------------------------------------------------

export function useInventoryImportPreview(): ReturnType<
  typeof useMutation<ImportPreviewResponse, Error, ImportPreviewInput>
> {
  const qc = useQueryClient();
  return useMutation<ImportPreviewResponse, Error, ImportPreviewInput>({
    mutationFn: ({ file }) => {
      // multipart/form-data — api.postForm lässt den Browser die boundary setzen.
      const form = new FormData();
      form.append('file', file);
      return api.postForm<ImportPreviewResponse>('/inventory/import/preview', form);
    },
    onSuccess: () => {
      // Preview persistiert eine neue Session -> Verlauf aktualisieren.
      void qc.invalidateQueries({ queryKey: inventoryImportKeys.sessions });
    },
  });
}

export function useInventoryImportApply(): ReturnType<
  typeof useMutation<ImportApplyResult, Error, ImportApplyInput>
> {
  const qc = useQueryClient();
  return useMutation<ImportApplyResult, Error, ImportApplyInput>({
    mutationFn: ({ sessionId, accept }) =>
      api.post<ImportApplyResult, { accept: string[] }>(
        `/inventory/import/${sessionId}/apply`,
        { accept }
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: inventoryImportKeys.sessions });
      void qc.invalidateQueries({ queryKey: inventoryImportKeys.session(vars.sessionId) });
      // Apply legt Kunden/Standorte/Systeme an bzw. ändert sie — die
      // Stammdaten-Caches invalidieren, sonst zeigen Listen/Details bis zum
      // manuellen Refresh veraltete Daten (Standort-Keys hängen unter
      // ['customers', 'detail', ...] und sind damit mit abgedeckt).
      void qc.invalidateQueries({ queryKey: queryKeys.customers.all });
      void qc.invalidateQueries({ queryKey: queryKeys.systems.all });
    },
  });
}

// --- Bitwarden-Map-Upload (S8-06, Regel-2-Lockerung 12.08.2026) -------------
// POST /inventory/bw-map nimmt die lokal am Sysadmin-PC erzeugte bw-map.tsv
// (host<TAB>item_id[<TAB>user]) entgegen und pflegt NUR die Item-IDs zentral
// in systems.bw_item_id. bw CLI, BW_SESSION und Secrets bleiben lokal; die
// Auflösung ID -> Passwort macht ausschließlich der Client zur Laufzeit.
// DTOs handgepflegt (kompakte Namen); Drift gegen api.gen.ts fangen die
// Wire-Checks oben ab (BwMapUploadResult ist dort aufgenommen).

export interface BwMapInvalidLine {
  /** 1-basierte Zeilennummer in der hochgeladenen Datei. */
  line: number;
  reason: string;
}

export interface BwMapUploadResult {
  updated: number;
  unchanged: number;
  /** Hosts ohne passendes System (Alias/Hostname/FQDN) — bleiben unverändert. */
  unmatched: string[];
  invalid: BwMapInvalidLine[];
}

export interface BwMapUploadInput {
  file: File;
}

export function useBwMapUpload(): ReturnType<
  typeof useMutation<BwMapUploadResult, Error, BwMapUploadInput>
> {
  const qc = useQueryClient();
  return useMutation<BwMapUploadResult, Error, BwMapUploadInput>({
    mutationFn: ({ file }) => {
      // Upload-Muster wie useInventoryImportPreview: multipart, Feld 'file'.
      const form = new FormData();
      form.append('file', file);
      return api.postForm<BwMapUploadResult>('/inventory/bw-map', form);
    },
    onSuccess: () => {
      // bw_item_id hängt an Systemen -> kompletten systems-Baum neu laden,
      // damit Listen und Detailseiten die Verknüpfung sofort zeigen.
      void qc.invalidateQueries({ queryKey: queryKeys.systems.all });
    },
  });
}
